import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import usePrivateLink from "../../config/usePrivateLink";

/* =====================================================
   HELPERS
===================================================== */

const normalizePath = (path = "") => {
  const value = String(path || "/")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");

  return value || "/";
};

const isTabActive = (tab, pathname) => {
  const current = normalizePath(pathname);

  const paths =
    tab.activePaths?.length > 0 ? [...tab.activePaths, tab.path] : [tab.path];

  return paths.some((path) => {
    if (!path) return false;

    return !!matchPath({ path: normalizePath(path), end: true }, current);
  });
};

const getClient = () => {
  try {
    return JSON.parse(localStorage.getItem("client") || "{}");
  } catch {
    return {};
  }
};

/* =====================================================
   COMPONENT
===================================================== */

export default function ModuleHeader({
  tabs = [],
  title = "Academic Management",
  subtitle = "Manage your academic operations",
}) {
  const hasAccess = usePrivateLink();
  const { pathname } = useLocation();

  const scrollRef = useRef(null);
  const activeTabRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const [canScrollRight, setCanScrollRight] = useState(false);

  const client = getClient();

  /* =====================================================
     FILTER ACCESSIBLE TABS
  ===================================================== */

  const filteredTabs = tabs.filter((tab) => {
    const visible =
      typeof tab.visibility === "function" ? tab.visibility(client) : true;

    const hasPermission = hasAccess(
      tab.external ? tab.accessPath || tab.path : tab.path,
    );

    return visible && hasPermission;
  });

  const activeTab = filteredTabs.find((tab) => isTabActive(tab, pathname));

  /* =====================================================
     SCROLL HANDLING
  ===================================================== */

  const updateScrollState = () => {
    const container = scrollRef.current;

    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > 4);

    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  const scrollTabs = (direction) => {
    const container = scrollRef.current;

    if (!container) return;

    container.scrollBy({
      left: direction * 260,
      behavior: "smooth",
    });
  };

  /* =====================================================
     AUTO SCROLL ACTIVE TAB INTO VIEW
  ===================================================== */

  useEffect(() => {
    const container = scrollRef.current;
    const active = activeTabRef.current;

    if (!container || !active) return;

    const targetLeft =
      active.offsetLeft -
      container.offsetLeft -
      container.clientWidth / 2 +
      active.clientWidth / 2;

    container.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: "smooth",
    });
  }, [pathname]);

  useEffect(() => {
    updateScrollState();

    const container = scrollRef.current;

    if (!container) return;

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollState)
        : null;

    if (observer) {
      observer.observe(container);

      if (container.firstElementChild) {
        observer.observe(container.firstElementChild);
      }
    }

    window.addEventListener("resize", updateScrollState);

    return () => {
      observer?.disconnect();

      window.removeEventListener("resize", updateScrollState);
    };
  }, [pathname, filteredTabs.length]);

  if (!filteredTabs.length) return null;

  return (
    <div className="col-12 academic-module-wrapper">
      <style>{`
        /* ============================================
           ACADEMIC MODULE HEADER
        ============================================ */

        .academic-module-wrapper {
          width: 100%;
          min-width: 0;
          margin-bottom: 20px;
          font-family: inherit;
        }

        .academic-module-card {
          position: relative;
          background: #ffffff;
          border: 1px solid #e7ebf2;
          border-radius: 15px;
          box-shadow: 0 3px 16px rgba(24, 39, 75, 0.035);
          overflow: hidden;
        }

        /* ============================================
           TOP HEADING
        ============================================ */

        .academic-module-top {
          padding: 15px 20px 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          border-bottom: 1px solid #edf0f5;
        }

        .academic-module-heading {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .academic-module-heading-icon {
          width: 39px;
          height: 39px;
          flex-shrink: 0;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #edf2ff;
          color: #5369dd;
          font-size: 21px;
        }

        .academic-module-heading-text {
          min-width: 0;
        }

        .academic-module-title {
          font-size: 15px;
          font-weight: 750;
          color: #27324a;
          margin: 0 0 2px;
          letter-spacing: -0.25px;
          line-height: 1.3;
        }

        .academic-module-subtitle {
          font-size: 11.5px;
          color: #8590a3;
          margin: 0;
          line-height: 1.4;
        }

        .academic-module-active-label {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 11px;
          font-weight: 600;
          color: #5268dc;
          background: #f0f3ff;
          border-radius: 8px;
          padding: 7px 11px;
          white-space: nowrap;
        }

        .academic-module-active-label i {
          font-size: 15px;
        }

        /* ============================================
           NAVIGATION AREA
        ============================================ */

        .academic-module-nav {
          display: flex;
          align-items: center;
          position: relative;
          min-width: 0;
          padding: 7px 12px;
          background: #fff;
        }

        .academic-module-scroll {
          display: flex;
          align-items: stretch;
          flex: 1;
          gap: 5px;
          min-width: 0;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          scroll-behavior: smooth;
          scroll-padding: 10px;
        }

        .academic-module-scroll::-webkit-scrollbar {
          display: none;
        }

        /* ============================================
           NAVIGATION TABS
        ============================================ */

        .academic-module-tab {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          padding: 11px 16px;
          min-height: 43px;
          flex-shrink: 0;
          border-radius: 9px;
          text-decoration: none !important;
          color: #667187;
          font-size: 12.5px;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
          transition:
            background 0.2s ease,
            color 0.2s ease,
            box-shadow 0.2s ease;
          border: 1px solid transparent;
          outline-offset: 3px;
        }

        .academic-module-tab i {
          font-size: 18px;
          line-height: 1;
          flex-shrink: 0;
        }

        .academic-module-tab:hover {
          background: #f5f7fd;
          color: #5369dc;
          border-color: #ecf0fc;
        }

        .academic-module-tab:focus-visible {
          outline: 2px solid #5369dc;
        }

        /* ============================================
           ACTIVE TAB
        ============================================ */

        .academic-module-tab.active {
          color: #ffffff;
          background: linear-gradient(
            135deg,
            #6578ed 0%,
            #5367d7 100%
          );
          box-shadow:
            0 3px 8px rgba(83, 103, 215, 0.2);
          border-color: transparent;
          font-weight: 650;
        }

        .academic-module-tab.active:hover {
          color: #ffffff;
          background: #5367d7;
        }

        .academic-module-tab.active::after {
          content: "";
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 12px;
          height: 2px;
          border-radius: 2px;
          background: rgba(255, 255, 255, 0.65);
        }

        /* ============================================
           SCROLL BUTTONS
        ============================================ */

        .academic-module-scroll-btn {
          width: 30px;
          height: 34px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f4f6fb;
          border: 1px solid #e9ecf3;
          border-radius: 8px;
          cursor: pointer;
          color: #606d84;
          font-size: 17px;
          padding: 0;
          transition: 0.2s ease;
        }

        .academic-module-scroll-btn:hover {
          background: #eef2ff;
          border-color: #dce4ff;
          color: #5369dc;
        }

        .academic-module-scroll-btn:focus-visible {
          outline: 2px solid #5369dc;
        }

        .academic-module-scroll-btn.left {
          margin-right: 8px;
        }

        .academic-module-scroll-btn.right {
          margin-left: 8px;
        }

        /* ============================================
           TABLET
        ============================================ */

        @media (max-width: 991px) {
          .academic-module-top {
            padding: 13px 16px;
          }

          .academic-module-nav {
            padding: 7px 10px;
          }

          .academic-module-tab {
            padding: 10px 13px;
            font-size: 12px;
          }

          .academic-module-tab i {
            font-size: 17px;
          }
        }

        /* ============================================
           MOBILE
        ============================================ */

        @media (max-width: 575px) {
          .academic-module-wrapper {
            margin-bottom: 14px;
          }

          .academic-module-card {
            border-radius: 12px;
          }

          .academic-module-top {
            padding: 11px 12px;
          }

          .academic-module-heading {
            gap: 9px;
          }

          .academic-module-heading-icon {
            width: 35px;
            height: 35px;
            border-radius: 9px;
            font-size: 19px;
          }

          .academic-module-title {
            font-size: 13.5px;
          }

          .academic-module-subtitle {
            font-size: 10px;
          }

          .academic-module-active-label {
            display: none;
          }

          .academic-module-nav {
            padding: 7px 8px;
          }

          .academic-module-scroll {
            gap: 4px;
          }

          .academic-module-tab {
            padding: 9px 12px;
            font-size: 11.5px;
            min-height: 40px;
            gap: 7px;
          }

          .academic-module-tab i {
            font-size: 16px;
          }

          .academic-module-scroll-btn {
            width: 25px;
            height: 31px;
            font-size: 15px;
          }

          .academic-module-scroll-btn.left {
            margin-right: 5px;
          }

          .academic-module-scroll-btn.right {
            margin-left: 5px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .academic-module-tab,
          .academic-module-scroll-btn {
            transition: none;
          }

          .academic-module-scroll {
            scroll-behavior: auto;
          }
        }
      `}</style>

      <div className="academic-module-card">
        {/* ============================================
            HEADER
        ============================================ */}

        {/* <div className="academic-module-top">
          <div className="academic-module-heading">
            <div className="academic-module-heading-icon">
              <i className="ri-graduation-cap-line" />
            </div>

            <div className="academic-module-heading-text">
              <h5 className="academic-module-title">
                {title}
              </h5>

              <p className="academic-module-subtitle">
                {subtitle}
              </p>
            </div>
          </div>

          {activeTab && (
            <div className="academic-module-active-label">
              <i className={activeTab.icon} />
              <span>{activeTab.title}</span>
            </div>
          )}
        </div> */}

        {/* ============================================
            NAVIGATION
        ============================================ */}

        <nav
          className="academic-module-nav"
          aria-label="Academic module navigation"
        >
          {canScrollLeft && (
            <button
              type="button"
              className="academic-module-scroll-btn left"
              onClick={() => scrollTabs(-1)}
              title="Scroll left"
              aria-label="Scroll menu left"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          )}

          <div
            className="academic-module-scroll"
            ref={scrollRef}
            onScroll={updateScrollState}
          >
            {filteredTabs.map((tab) => {
              const active = !tab.external && isTabActive(tab, pathname);

              const className =
                "academic-module-tab" + (active ? " active" : "");

              const content = (
                <>
                  <i className={tab.icon} aria-hidden="true" />
                  <span>{tab.title}</span>

                  {tab.external && (
                    <i className="ri-external-link-line" aria-hidden="true" />
                  )}
                </>
              );

              // External tabs open in a new browser tab.
              if (tab.external) {
                return (
                  <a
                    key={tab.id}
                    href={tab.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={tab.description || tab.title}
                    className={className}
                  >
                    {content}
                  </a>
                );
              }

              // Existing internal navigation remains unchanged.
              return (
                <Link
                  key={tab.id}
                  ref={active ? activeTabRef : null}
                  to={tab.path}
                  title={tab.description || tab.title}
                  aria-current={active ? "page" : undefined}
                  className={className}
                  onClick={(event) => {
                    // Prevent redundant navigation to the current route.
                    if (active) {
                      event.preventDefault();
                    }
                  }}
                >
                  {content}
                </Link>
              );
            })}
          </div>

          {canScrollRight && (
            <button
              type="button"
              className="academic-module-scroll-btn right"
              onClick={() => scrollTabs(1)}
              title="Scroll right"
              aria-label="Scroll menu right"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}
